/**
 * Resolve a YouTube URL to its human title, cached across the whole app.
 *
 * The title comes from oEmbed (see `fetchVideoTitle`). A module-level cache plus
 * an in-flight map mean each distinct video is fetched at most once no matter how
 * many cards reference it — a queue full of the same request, or the same item
 * showing in both "now playing" and the list, costs one request.
 *
 * Returns null until the title resolves (or if it can't); callers fall back to
 * `displayTitle`.
 */
import { useEffect, useState } from "react";
import { fetchVideoTitle, videoId } from "../lib/youtube";

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

export function useVideoTitle(url: string): string | null {
  const key = videoId(url) ?? url;
  const [title, setTitle] = useState<string | null>(() => cache.get(key) ?? null);

  useEffect(() => {
    const cached = cache.get(key);
    if (cached) {
      setTitle(cached);
      return;
    }

    let active = true;
    let promise = inflight.get(key);
    if (!promise) {
      promise = fetchVideoTitle(url).then((t) => {
        if (t) cache.set(key, t);
        inflight.delete(key);
        return t;
      });
      inflight.set(key, promise);
    }
    promise.then((t) => {
      if (active) setTitle(t);
    });

    return () => {
      active = false;
    };
  }, [key, url]);

  return title;
}
